import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTE_DQF } from '../driverQualification-routing.module';
import { ROUTE_DQF_BACKGROUND_CHECK_PROCESSING, ROUTE_DRIVER_DASHBOARD } from 'src/app/shared/routes';
import dayjs from 'dayjs';
import { of, interval } from 'rxjs';
import { switchMap, tap, takeWhile, catchError, finalize } from 'rxjs/operators';
import { DriverQualificationService } from '../service/driver-qualification.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { MatDialog } from '@angular/material/dialog';
import { PccInstructionsDialogComponent } from '../pcc-instructions-dialog/pcc-instructions-dialog.component';

const DOCUMENT_TYPE = 'Criminal Record (Background) Check';
const SOURCE_TEXT = 'self';
const REPORT_TYPE = 'PoliceClearance';
const EXTENSION = 'pdf';

@Component({
    selector: 'app-stripe-redirect',
    templateUrl: './stripe-redirect.component.html',
    styleUrls: ['./stripe-redirect.component.scss'],
    standalone: false
})
export class StripeRedirectComponent implements OnInit {
  initiateStripe: any;
  documentName: any;
  loading: boolean;
  paymentOrders: any[];
  dqfId: string;
  driver: any;

  constructor(
    private readonly router: Router,
    private route: ActivatedRoute,
    private dqfService: DriverQualificationService,
    private readonly toastService: ToastService,
    private readonly registrationService: RegistrationService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.documentName = params.documentType;
      const sessionId = params?.session_id;

      if (sessionId) {
        this.verifyStripePayment(sessionId);
      } else {
        this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_BACKGROUND_CHECK_PROCESSING}`], {
          queryParams: { documentType: DOCUMENT_TYPE, stripe: true }
        });
      }
    });
    this.driver = this.registrationService.getDriver();
    this.dqfId = this.dqfService.dqfId;
  }

  verifyStripePayment(sessionId: string): void {
    this.loading = true;

    const stripeCheckoutSessionResponse = JSON.parse(sessionStorage.getItem('stripeCheckoutSessionResponse') || '{}');
    const orderId = stripeCheckoutSessionResponse?.orderId;

    if (!orderId) {
      console.error('No orderId found in session');
      this.loading = false;
      return;
    }

    this.dqfService
      .getPaymentStatus(sessionId)
      .pipe(
        switchMap((response: any) => {
          if (response?.paidStatus === 'paid') {
            this.initiateReportGeneration();
            return of(null); // done
          }

          // Start polling getStripeOrders() if unpaid
          return interval(1000).pipe(
            switchMap(() => this.dqfService.getStripeOrders(this.driver.id)),
            tap((orders: any[]) => {
              this.paymentOrders = orders;
            }),
            takeWhile((orders: any[]) => {
              const targetOrder = orders.find(order => order.id === orderId);
              if (!targetOrder) return true;

              if (
                targetOrder.status === 'fulfilled' ||
                targetOrder.status === 'complete'
              ) {
                this.initiateReportGeneration();
                return false;
              }

              if (targetOrder.status === 'cancelled') {
                this.toastService.showError('Payment failed. Please try again.');
                return false;
              }

              // Keep polling while order is still in-progress.
              return targetOrder.status === 'created' || targetOrder.status === 'awaitingFulfillment';
            }, true),
            catchError(err => {
              return of(null);
            })
          );
        }),
        finalize(() => {
          this.loading = false;
          sessionStorage.removeItem('stripeCheckoutSessionResponse');
        })
      )
      .subscribe();
  }

  initiateReportGeneration() {
    this.loading = true;
    const generationDate = new Date(dayjs().toISOString()).setMilliseconds(0);
    const dateISOString = new Date(generationDate).toISOString().replace('.000Z', 'Z');
    const orderPccOutsideofDQF = sessionStorage.getItem('orderPccOutsideDqf');
    let model = {};
    if (!orderPccOutsideofDQF) {
      model = {
        dqfId: this.dqfId,
        reportType: REPORT_TYPE,
        extension: EXTENSION,
        source: SOURCE_TEXT,
        generationDate: dateISOString,
        uploadType: 'request'
      };
    } else {
      const reportDataStr = sessionStorage.getItem('pccReportData');
      const reportData = JSON.parse(reportDataStr);
      model = {
        reportType: REPORT_TYPE,
        extension: EXTENSION,
        source: SOURCE_TEXT,
        generationDate: dateISOString,
        uploadType: 'request',
        ...reportData
      };
    }

    this.dqfService
      .initiateReportGeneration(this.driver.id, model)
      .pipe(
        catchError((error: any) => {
          this.loading = false;
          this.toastService.showError("Sorry, Couldn't initiate report generation");
          return error;
        })
      )
      .subscribe((response: any) => {
        this.loading = false;
        if (response) {
          this.toastService.showSuccess('Successfully initiated report generation');
        }
        setTimeout(() => {
          this.loadDriver();

          if (!orderPccOutsideofDQF) {
            this.dialog
              .open(PccInstructionsDialogComponent, {
                width: '450px',
                disableClose: true
              })
              .afterClosed()
              .subscribe(() => {
                this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_BACKGROUND_CHECK_PROCESSING}`], {
                  queryParams: { documentType: DOCUMENT_TYPE }
                });
              });
          } else {
            sessionStorage.removeItem('orderPccOutsideDqf');
            sessionStorage.removeItem('pccReportData');

            this.dialog
              .open(PccInstructionsDialogComponent, {
                width: '450px',
                disableClose: true
              })
              .afterClosed()
              .subscribe(() => {
                this.router.navigate([ROUTE_DRIVER_DASHBOARD]);
              });
          }
        }, 1000);
      });
  }
  loadDriver() {
    this.registrationService
      .loadDriver()
      .pipe()
      .subscribe();
  }
}
